import axios from "axios"

import { api } from "@/lib/api-client"

// =============================================================================
// Fair Use — the current user's monthly AI-budget surface (issue #2625,
// spec section 6/7). User endpoints live under /api/me/usage* and are
// `@authenticated` (routes/me_usage.py). The composer indicator reads the
// snapshot below; live updates arrive via the `fair_use` WS metadata routed in
// features/fair-use/fair-use-ws-bridge.ts — deliberately no polling.
// =============================================================================

export type MyUsageStatus = "ok" | "warn" | "downgrade" | "block"

export type FairUseTier = "standard" | "pro" | "unlimited"

/** Display-only: a legacy raw SEK override with no named plan. */
export type FairUsePlan = FairUseTier | "custom"

/**
 * The 12 per-plan entitlement values (fair use round 2, #3087).
 *
 * `null` means "no limit" and is only ever sent for the four fields that
 * allow it (`scheduled_flows`, `flow_max_steps`, `flow_max_seconds`,
 * `images_per_month`) — the backend normalizes every row to be complete, so
 * a present row always carries all twelve keys.
 */
export type PlanEntitlements = {
  max_reasoning_effort: string
  deep: boolean
  voice: boolean
  scheduled_flows: number | null
  flow_max_steps: number | null
  flow_max_seconds: number | null
  images_per_month: number | null
  max_tool_iterations: number
  turn_max_runtime_seconds: number
  deep_token_budget: number
  deep_recursion_limit: number
  capacity_retry_budget: number
}

/**
 * `/api/config`'s `fair_use` block. ALWAYS present on a current backend —
 * `{enabled: false, plan: null, entitlements: null}` when fair use is off or
 * unwired — so surfaces branch on one shape. Optional here only so older
 * backends and test fixtures type-check.
 */
export type ConfigFairUse = {
  enabled: boolean
  plan: FairUseTier | null
  entitlements: PlanEntitlements | null
}

/**
 * The structured refusal every plan-locked feature sends
 * (`plan_upgrade_payload` in helloai_utilities/fair_use/entitlements.py).
 *
 * `error` and `code` deliberately carry the SAME string so a WS frame, an
 * HTTP body and the durable runner's code matcher all detect it the same way.
 * `feature` is the seam that refused: `"deep"`, `"voice"`, `"model"`,
 * `"images"`, `"scheduled_flows"`, `"flow_max_steps"` or `"flow_max_seconds"`.
 * `limit`/`used` ride along only on the counted caps.
 */
export type PlanUpgradePayload = {
  error: "plan_upgrade_required"
  code: "plan_upgrade_required"
  feature: string
  plan?: string
  required_plans?: string[]
  limit?: number
  used?: number
  message?: string
}

export type MyUsageRequestState = {
  /** True from the request-CTA threshold (default 90 %) upward. */
  allowed: boolean
  /** True when the user already has a pending budget request this month. */
  pending: boolean
}

/** GET /api/me/usage response (spec section 6). */
export type MyUsage = {
  enabled: boolean
  year_month: string
  /**
   * Spend so far this month, in SEK.
   *
   * `null` is a CLIENT-SIDE value only — the endpoint always sends a number.
   * It means "this figure is known to be stale": a live WS merge moved the
   * percent while carrying no cost of its own, so the last fetched amount no
   * longer belongs next to it (see `mergeFairUseWsMetadata`). Surfaces must
   * omit the amount rather than print it; `formatSek` renders "-" for it.
   */
  cost_sek: number | null
  cap_sek: number
  percent: number
  status: MyUsageStatus
  /** Named plan. Absent on older backends — treat as standard. */
  tier?: FairUsePlan
  reset_at: string
  /**
   * Configured "Request more" floor, same 2-decimal rounding as `percent`.
   * Absent on older backends and WS-only merges — callers default to 90.
   */
  request_cta_percent?: number
  /**
   * Absent on the disabled GET stub (`{enabled: false}`) and on any cache
   * entry that has not yet been filled by GET /api/me/usage. Surfaces must
   * optional-chain; a missing `request` is not a pending request.
   */
  request?: MyUsageRequestState
  /**
   * The user's resolved plan row (fair use round 2). Absent when the endpoint
   * answered `{enabled: false}` and on every WS-only merge, so surfaces must
   * optional-chain rather than assume a row.
   */
  plan_entitlements?: PlanEntitlements
}

/**
 * Live `fair_use` metadata on the final WS message of an answer.
 *
 * `percent`/`status` are the movement; `cost_sek`/`cap_sek` are the money
 * behind it, which both producers (`approaches/approach.py` and
 * `approaches/explorerdeep/explorer_deep.py`) append to the final chunk so the
 * popover cannot show a fresh percent above a stale amount. They stay OPTIONAL
 * here: a deployment running an older backend sends `{percent, status}` alone,
 * and the merge treats their absence as "the cost is now unknown".
 */
export type FairUseWsMetadata = {
  percent: number
  status: MyUsageStatus
  cost_sek?: number
  cap_sek?: number
}

// The query key for this snapshot lives in the shared registry:
// queryKeys.fairUseMyUsage (src/lib/query-keys.ts).

export async function fetchMyUsage(): Promise<MyUsage> {
  const response = await api.get("/me/usage")
  return response.data || response
}

export type CreateMyUsageRequestResult =
  | { outcome: "created" }
  /** The backend returned 409 — a request for this month is already pending. */
  | { outcome: "already_pending" }

/**
 * POST /api/me/usage/request — create a pending request-more budget request.
 * A 409 (one already pending) is a normal outcome for the dialog, not an
 * error, so it is mapped instead of thrown.
 */
export async function createMyUsageRequest(
  motivation?: string
): Promise<CreateMyUsageRequestResult> {
  const trimmed = motivation?.trim()
  try {
    await api.post("/me/usage/request", trimmed ? { motivation: trimmed } : {})
    return { outcome: "created" }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return { outcome: "already_pending" }
    }
    throw error
  }
}
